import { createTheme, alpha, ThemeOptions } from '@mui/material/styles';
import { PaletteMode } from '@mui/material';

// Define theme settings based on mode
export const getDesignTokens = (mode: PaletteMode): ThemeOptions => ({
  palette: {
    mode,
    ...(mode === 'light'
      ? {
          // Light mode colors
          primary: {
            main: '#0066CC',
            light: '#4D94FF',
            dark: '#004C99',
            contrastText: '#FFFFFF'
          },
          secondary: {
            main: '#3D5AFE',
            light: '#8187FF',
            dark: '#0031CA',
            contrastText: '#FFFFFF'
          },
          error: {
            main: '#D32F2F',
            light: '#EF5350',
            dark: '#C62828',
          },
          warning: {
            main: '#F57C00',
            light: '#FFB74D',
            dark: '#E65100',
          },
          info: {
            main: '#0288D1',
            light: '#4FC3F7',
            dark: '#01579B',
          },
          success: {
            main: '#2E7D32',
            light: '#66BB6A',
            dark: '#1B5E20',
          },
          background: {
            default: '#F5F7FA',
            paper: '#FFFFFF'
          },
          text: {
            primary: '#1A2027',
            secondary: '#637381'
          },
          divider: '#E5E8EC',
          action: {
            active: '#637381',
            hover: alpha('#0066CC', 0.04),
            selected: alpha('#0066CC', 0.08),
            disabled: '#C4CDD5',
            disabledBackground: '#F9FAFB',
          },
        }
      : {
          // Dark mode colors
          primary: {
            main: '#5090EA',
            light: '#77A7EF',
            dark: '#2D5FAD',
            contrastText: '#FFFFFF'
          },
          secondary: {
            main: '#6B7CFF',
            light: '#9AA5FF',
            dark: '#455BD1',
            contrastText: '#FFFFFF'
          },
          error: {
            main: '#F44336',
            light: '#FF7961',
            dark: '#BA000D',
          },
          warning: {
            main: '#FF9800',
            light: '#FFB74D',
            dark: '#C77700',
          },
          info: {
            main: '#29B6F6',
            light: '#4FC3F7',
            dark: '#0288D1',
          },
          success: {
            main: '#4CAF50',
            light: '#80E27E',
            dark: '#087F23',
          },
          background: {
            default: '#121212',
            paper: '#1E1E1E'
          },
          text: {
            primary: '#FFFFFF',
            secondary: '#B0BEC5'
          },
          divider: '#303030',
          action: {
            active: '#B0BEC5',
            hover: alpha('#5090EA', 0.08),
            selected: alpha('#5090EA', 0.16),
            disabled: '#666666',
            disabledBackground: '#2C2C2C',
          },
        }),
  },
  typography: {
    fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif',
    h1: {
      fontSize: '2.5rem',
      fontWeight: 600,
      lineHeight: 1.2,
      letterSpacing: '-0.02em',
    },
    h2: {
      fontSize: '2rem',
      fontWeight: 600,
      lineHeight: 1.3,
      letterSpacing: '-0.01em',
    },
    h3: {
      fontSize: '1.5rem',
      fontWeight: 600,
      lineHeight: 1.4,
    },
    h4: {
      fontSize: '1.25rem',
      fontWeight: 600,
      lineHeight: 1.4,
    },
    h5: {
      fontSize: '1.125rem',
      fontWeight: 600,
      lineHeight: 1.5,
    },
    h6: {
      fontSize: '1rem',
      fontWeight: 600,
      lineHeight: 1.5,
    },
    body1: {
      fontSize: '1rem',
      lineHeight: 1.5,
    },
    body2: {
      fontSize: '0.875rem',
      lineHeight: 1.57,
    },
    subtitle1: {
      fontSize: '1rem',
      fontWeight: 500,
      lineHeight: 1.5,
    },
    subtitle2: {
      fontSize: '0.875rem',
      fontWeight: 500,
      lineHeight: 1.57,
    },
    button: {
      fontSize: '0.875rem',
      fontWeight: 600,
      lineHeight: 1.75,
      textTransform: 'none',
    },
    caption: {
      fontSize: '0.75rem',
      lineHeight: 1.66,
    },
    overline: {
      fontSize: '0.75rem',
      fontWeight: 600,
      letterSpacing: '0.5px',
      lineHeight: 2.5,
      textTransform: 'uppercase',
    },
  },
  shape: {
    borderRadius: 8,
  },
  shadows: [
    'none',
    '0px 2px 1px -1px rgba(0,0,0,0.05),0px 1px 1px 0px rgba(0,0,0,0.03),0px 1px 3px 0px rgba(0,0,0,0.05)',
    '0px 3px 3px -2px rgba(0,0,0,0.06),0px 3px 4px 0px rgba(0,0,0,0.04),0px 1px 8px 0px rgba(0,0,0,0.06)',
    '0px 3px 5px -1px rgba(0,0,0,0.06),0px 6px 10px 0px rgba(0,0,0,0.04),0px 1px 18px 0px rgba(0,0,0,0.06)',
    '0px 4px 5px -2px rgba(0,0,0,0.06),0px 7px 10px 1px rgba(0,0,0,0.04),0px 2px 16px 1px rgba(0,0,0,0.06)',
    '0px 5px 5px -3px rgba(0,0,0,0.06),0px 8px 10px 1px rgba(0,0,0,0.04),0px 3px 14px 2px rgba(0,0,0,0.06)',
    '0px 5px 6px -3px rgba(0,0,0,0.06),0px 9px 12px 1px rgba(0,0,0,0.04),0px 3px 16px 2px rgba(0,0,0,0.06)',
    '0px 6px 6px -3px rgba(0,0,0,0.06),0px 10px 14px 1px rgba(0,0,0,0.04),0px 4px 18px 3px rgba(0,0,0,0.06)',
    '0px 6px 7px -4px rgba(0,0,0,0.06),0px 11px 15px 1px rgba(0,0,0,0.04),0px 4px 20px 3px rgba(0,0,0,0.06)',
    '0px 7px 8px -4px rgba(0,0,0,0.06),0px 12px 17px 2px rgba(0,0,0,0.04),0px 5px 22px 4px rgba(0,0,0,0.06)',
    '0px 7px 8px -4px rgba(0,0,0,0.06),0px 13px 19px 2px rgba(0,0,0,0.04),0px 5px 24px 4px rgba(0,0,0,0.06)',
    '0px 7px 9px -4px rgba(0,0,0,0.06),0px 14px 21px 2px rgba(0,0,0,0.04),0px 5px 26px 4px rgba(0,0,0,0.06)',
    '0px 8px 9px -5px rgba(0,0,0,0.06),0px 15px 22px 2px rgba(0,0,0,0.04),0px 6px 28px 5px rgba(0,0,0,0.06)',
    '0px 8px 10px -5px rgba(0,0,0,0.06),0px 16px 24px 2px rgba(0,0,0,0.04),0px 6px 30px 5px rgba(0,0,0,0.06)',
    '0px 8px 11px -5px rgba(0,0,0,0.06),0px 17px 26px 2px rgba(0,0,0,0.04),0px 6px 32px 5px rgba(0,0,0,0.06)',
    '0px 9px 11px -5px rgba(0,0,0,0.06),0px 18px 28px 2px rgba(0,0,0,0.04),0px 7px 34px 6px rgba(0,0,0,0.06)',
    '0px 9px 12px -6px rgba(0,0,0,0.06),0px 19px 29px 2px rgba(0,0,0,0.04),0px 7px 36px 6px rgba(0,0,0,0.06)',
    '0px 10px 13px -6px rgba(0,0,0,0.06),0px 20px 31px 3px rgba(0,0,0,0.04),0px 8px 38px 7px rgba(0,0,0,0.06)',
    '0px 10px 13px -6px rgba(0,0,0,0.06),0px 21px 33px 3px rgba(0,0,0,0.04),0px 8px 40px 7px rgba(0,0,0,0.06)',
    '0px 10px 14px -6px rgba(0,0,0,0.06),0px 22px 35px 3px rgba(0,0,0,0.04),0px 8px 42px 7px rgba(0,0,0,0.06)',
    '0px 11px 14px -7px rgba(0,0,0,0.06),0px 23px 36px 3px rgba(0,0,0,0.04),0px 9px 44px 8px rgba(0,0,0,0.06)',
    '0px 11px 15px -7px rgba(0,0,0,0.06),0px 24px 38px 3px rgba(0,0,0,0.04),0px 9px 46px 8px rgba(0,0,0,0.06)',
    '0px 12px 16px -7px rgba(0,0,0,0.06),0px 25px 40px 4px rgba(0,0,0,0.04),0px 10px 48px 8px rgba(0,0,0,0.06)',
    '0px 12px 17px -8px rgba(0,0,0,0.06),0px 26px 42px 4px rgba(0,0,0,0.04),0px 10px 50px 9px rgba(0,0,0,0.06)',
    '0px 13px 18px -8px rgba(0,0,0,0.06),0px 27px 44px 4px rgba(0,0,0,0.04),0px 11px 52px 9px rgba(0,0,0,0.06)',
  ],
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        '*': {
          boxSizing: 'border-box',
        },
        html: {
          MozOsxFontSmoothing: 'grayscale',
          WebkitFontSmoothing: 'antialiased',
          height: '100%',
          width: '100%',
        },
        body: {
          height: '100%',
          width: '100%',
        },
        '#root': {
          height: '100%',
          width: '100%',
        },
        a: {
          textDecoration: 'none',
          color: 'inherit',
        },
        // Improved scrollbars for modern browsers
        '*::-webkit-scrollbar': {
          width: '8px',
          height: '8px',
        },
        '*::-webkit-scrollbar-track': {
          background: mode === 'light' ? '#F1F1F1' : '#2D2D2D',
        },
        '*::-webkit-scrollbar-thumb': {
          background: mode === 'light' ? '#C1C1C1' : '#555555',
          borderRadius: '4px',
        },
        '*::-webkit-scrollbar-thumb:hover': {
          background: mode === 'light' ? '#A8A8A8' : '#666666',
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          borderRadius: 8,
          fontWeight: 600,
          boxShadow: 'none',
          '&:hover': {
            boxShadow: '0px 3px 6px rgba(0, 0, 0, 0.1)',
          },
        },
        contained: {
          boxShadow: '0px 1px 3px rgba(0, 0, 0, 0.08)',
        },
        sizeSmall: {
          padding: '6px 16px',
        },
        sizeMedium: {
          padding: '8px 20px',
        },
        sizeLarge: {
          padding: '10px 24px',
        },
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: {
          backgroundColor: mode === 'light' ? '#FFFFFF' : '#1E1E1E',
          borderRight: `1px solid ${mode === 'light' ? '#E5E8EC' : '#303030'}`,
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundColor: mode === 'light' ? '#FFFFFF' : '#1E1E1E',
          color: mode === 'light' ? '#1A2027' : '#FFFFFF',
          boxShadow: '0px 1px 3px rgba(0, 0, 0, 0.08)',
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.05)',
          transition: 'box-shadow 0.3s ease-in-out, transform 0.3s ease-in-out',
          '&:hover': {
            boxShadow: '0px 4px 8px rgba(0, 0, 0, 0.1)',
            transform: 'translateY(-2px)',
          },
        },
      },
    },
    MuiListItem: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          marginBottom: 4,
          '&.Mui-selected': {
            backgroundColor: alpha(mode === 'light' ? '#0066CC' : '#5090EA', 0.08),
            '&:hover': {
              backgroundColor: alpha(mode === 'light' ? '#0066CC' : '#5090EA', 0.12),
            },
          },
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundImage: 'none',
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          borderBottom: `1px solid ${mode === 'light' ? '#E5E8EC' : '#303030'}`,
          padding: '16px',
        },
        head: {
          fontWeight: 600,
          color: mode === 'light' ? '#1A2027' : '#FFFFFF',
          backgroundColor: mode === 'light' ? '#F9FAFB' : '#2D2D2D',
        },
      },
    },
    MuiLinearProgress: {
      styleOverrides: {
        root: {
          borderRadius: 3,
          overflow: 'hidden',
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontWeight: 500,
          fontSize: '0.875rem',
        },
      },
    },
  },
});

// Create the default light mode theme
const theme = createTheme(getDesignTokens('light'));

export default theme; 